/**
 * The webshop — the third branch of Småken. Unlike catering (a request that
 * gets confirmed) these are things anyone can buy outright and have shipped
 * or picked up: drinks, bakes and pantry goods from Småland.
 */

export type ShopCategory = 'Dryck' | 'Bakverk' | 'Skafferi' | 'Presentlådor';

export const SHOP_CATEGORIES: ShopCategory[] = ['Dryck', 'Bakverk', 'Skafferi', 'Presentlådor'];

export interface ShopProduct {
  id: string;
  name: string;
  category: ShopCategory;
  /** Who made it. Links to the place profile when `placeSlug` is set. */
  producer: string;
  placeSlug?: string;
  city: string;
  /** One line in the Småken voice — a tip, not a spec sheet. */
  tip: string;
  description: string;
  /** Price in SEK, incl. VAT. Consumers buy at this price. */
  price: number;
  unit: string;
  image: string;
  /** Shippable across Sweden, or pickup / local delivery only. */
  shipping: 'frakt' | 'endast-upphamtning';
  inStock: boolean;
  /** Editorial mark on the card. */
  stamp?: string;
}

export const SHOP_PRODUCTS: ShopProduct[] = [
  {
    id: 'sp-lingonsaft',
    name: 'Lingonsaft & flädersaft',
    category: 'Dryck',
    producer: 'Grönsakslandet',
    placeSlug: 'gronsakslandet',
    city: 'Ljungby',
    tip: 'Fläder till midsommar, lingon till allt annat. Ta båda.',
    description:
      'Två flaskor osötad koncentrerad saft, kokad på bär och blom plockade inom en mil från gården. 330 ml styck.',
    price: 189,
    unit: '2 flaskor',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/bfc5d9a0-2049-4a35-b378-fe08b7998bc1.jpg',
    shipping: 'frakt',
    inStock: true,
    stamp: 'BRA GREJ',
  },
  {
    id: 'sp-kaffe',
    name: 'Månadens böna',
    category: 'Dryck',
    producer: 'Kafferosteriet Kalmar',
    placeSlug: 'kafferosteriet-kalmar',
    city: 'Kalmar',
    tip: 'Rostad på tisdagen, hos dig på torsdagen. Bättre än så blir det inte.',
    description:
      'Hela bönor från ett enda ursprung, rostat ljust för bryggkaffe. Byts varje månad, så du får aldrig samma två gånger.',
    price: 145,
    unit: '250 g',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2cc0ea4e-1737-441e-b63f-03f5cb412f6e.jpg',
    shipping: 'frakt',
    inStock: true,
  },
  {
    id: 'sp-ol',
    name: 'Skogslager, fyrpack',
    category: 'Dryck',
    producer: 'Bryggeriet i Skogen',
    placeSlug: 'bryggeriet-i-skogen',
    city: 'Eksjö',
    tip: 'Ofiltrerad och grumlig. Precis som den ska vara.',
    description:
      'Fyra burkar ofiltrerad lager på 33 cl. Alkoholfri variant finns — välj i kassan.',
    price: 129,
    unit: '4 × 33 cl',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7d403927-1c90-4448-b890-d0f7d2b01b2f.jpg',
    shipping: 'endast-upphamtning',
    inStock: true,
  },
  {
    id: 'sp-bullar',
    name: 'Bullar hem till dörren',
    category: 'Bakverk',
    producer: 'Lilla Verket',
    placeSlug: 'lilla-verket',
    city: 'Växjö',
    tip: 'Beställ på söndagen. Ångra dig aldrig.',
    description:
      'Sex kanel- och kardemummabullar, gräddade samma morgon och packade varma. Levereras inom Växjö.',
    price: 179,
    unit: '6 st',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/aec883a4-55a3-4f72-9763-341c87017a93.jpg',
    shipping: 'endast-upphamtning',
    inStock: true,
    stamp: 'MEST SÅLD',
  },
  {
    id: 'sp-surdeg',
    name: 'Surdegslimpa',
    category: 'Bakverk',
    producer: 'Lilla Verket',
    placeSlug: 'lilla-verket',
    city: 'Växjö',
    tip: 'Skär den inte förrän den svalnat. Vi vet, det är svårt.',
    description: 'Bakad på svenskt lantvete med 24 timmars jäsning. Hämtas i butiken samma dag.',
    price: 65,
    unit: '1 limpa',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0c664b63-8d57-4f98-a722-f14bd09f5a50.jpg',
    shipping: 'endast-upphamtning',
    inStock: true,
  },
  {
    id: 'sp-kakor',
    name: 'Kakburk från konditoriet',
    category: 'Bakverk',
    producer: 'Askelyckans Konditori',
    placeSlug: 'askelyckan',
    city: 'Vetlanda',
    tip: 'Den där burken som aldrig hinner bli gammal.',
    description: 'Fem sorters småkakor bakade efter recept som inte ändrats på trettio år. Plåtburk ingår.',
    price: 215,
    unit: 'ca 40 kakor',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8fc743b5-2ac3-4a58-8c38-642530320605.jpg',
    shipping: 'frakt',
    inStock: true,
  },
  {
    id: 'sp-skafferi',
    name: 'Skafferitrio',
    category: 'Skafferi',
    producer: 'Grönsakslandet',
    placeSlug: 'gronsakslandet',
    city: 'Ljungby',
    tip: 'Honungen på ostbrickan, sylten till frukost, inläggningen till allt.',
    description: 'Lingonsylt, skogshonung och inlagd gurka. Tre burkar, inga konstigheter i innehållet.',
    price: 265,
    unit: '3 burkar',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ece9e12f-3c34-4ae9-8f4f-e4fefd2b12b3.jpg',
    shipping: 'frakt',
    inStock: true,
  },
  {
    id: 'sp-ost',
    name: 'Gårdsost, tre sorter',
    category: 'Skafferi',
    producer: 'Sörgårdens Mejeri',
    placeSlug: 'sorgardens-mejeri',
    city: 'Eksjö',
    tip: 'Ta fram dem en timme innan. Kall ost smakar ingenting.',
    description: 'Lagrad, kryddad och en färskost. Vakuumpackade och skickade kylda.',
    price: 320,
    unit: 'ca 600 g',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
    shipping: 'frakt',
    inStock: false,
  },
  {
    id: 'sp-present',
    name: 'Smålandslådan',
    category: 'Presentlådor',
    producer: 'Småken',
    city: 'Hela Småland',
    tip: 'Det här ger vi bort när vi vill göra intryck utan att anstränga oss.',
    description:
      'Kaffe, honung, sylt och ett bröd i en trälåda. Vår mest beställda present till kunder och medarbetare.',
    price: 495,
    unit: '1 låda',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8e1027e3-0b40-4974-8d2e-3451dcb6f350.jpg',
    shipping: 'frakt',
    inStock: true,
    stamp: 'TILL FÖRETAG',
  },
  {
    id: 'sp-fikapaket',
    name: 'Fikalådan',
    category: 'Presentlådor',
    producer: 'Top Notch Bakery',
    placeSlug: 'top-notch-bakery',
    city: 'Växjö',
    tip: 'Skicka den till någon som haft en tung vecka.',
    description: 'Veckans specialbulle, två bakverk och en påse kaffe. Packad för att tåla posten.',
    price: 285,
    unit: '1 låda',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/eebd77e7-cfe4-4b68-804b-72efaefe699e.jpg',
    shipping: 'frakt',
    inStock: true,
  },
];

export const shopByPlace = (slug: string): ShopProduct[] =>
  SHOP_PRODUCTS.filter((p) => p.placeSlug === slug);
